import React, {useMemo} from 'react';
import dayjs from 'dayjs';
import {useBalloonEventsMetadata, useQuery, useTranslations} from 'react-ui';
import {CategoryModel} from 'ts-core';
import {trackMixpanel} from '../utils/mixpanel';

type ActivePill =
    | {kind: 'category'; queryId: string; category: CategoryModel}
    | {kind: 'tag'; queryId: string; slug: string; name: string}
    | {kind: 'text'; queryId: string; text: string}
    | {kind: 'dates'; queryId: string; dates: Date[]};

const BALLOON_COLOR = 'rgb(191, 32, 136)';

const formatDateRange = (dates: Date[]): string => {
    if (dates.length === 0) return '';
    if (dates.length === 1) return dayjs(dates[0]).format('DD.MM.YYYY');
    const sorted = [...dates].sort((a, b) => a.getTime() - b.getTime());
    const first = dayjs(sorted[0]);
    const last = dayjs(sorted[sorted.length - 1]);
    if (first.isSame(last, 'year')) {
        return `${first.format('DD.MM.')} – ${last.format('DD.MM.YYYY')}`;
    }
    return `${first.format('DD.MM.YYYY')} – ${last.format('DD.MM.YYYY')}`;
};

/**
 * Mobile-only horizontal pill rail showing all currently active user filters
 * (text, dates, categories, tags). Each chip is removable via an inline ×,
 * matching the desktop tag-row pattern from FilterLabelCarousel. A leading
 * [Clear] pill removes everything visible in this row at once.
 */
export const ActiveFilterPills: React.FC = () => {
    const {queries, removeQueryById} = useQuery();
    const {metadata} = useBalloonEventsMetadata();
    const {translated, language} = useTranslations();

    const pills = useMemo<ActivePill[]>(() => {
        const result: ActivePill[] = [];
        for (const q of queries) {
            if (!q.id) continue;
            if (q.id === 'text' && q.text && q.text.trim() !== '') {
                result.push({kind: 'text', queryId: q.id, text: q.text});
                continue;
            }
            if (q.id === 'dates' && q.dates && q.dates.length > 0) {
                result.push({kind: 'dates', queryId: q.id, dates: q.dates});
                continue;
            }
            if (q.id.startsWith('tag:')) {
                const slug = q.tags?.[0] ?? q.id.slice(4);
                const tag = metadata.tags.find((t) => t.slug === slug);
                const name = tag?.name ?? (slug.charAt(0).toUpperCase() + slug.slice(1));
                result.push({kind: 'tag', queryId: q.id, slug, name});
                continue;
            }
            if (q.categories && q.categories.length > 0) {
                try {
                    const category = metadata.getCategoryById(q.categories[0]);
                    if (category) {
                        result.push({kind: 'category', queryId: q.id, category});
                    }
                } catch {
                    // getCategoryById may throw for stale ids; skip silently.
                }
            }
        }
        return result;
    }, [queries, metadata]);

    if (pills.length === 0) return null;

    const removePill = (pill: ActivePill) => {
        removeQueryById(pill.queryId);
        trackMixpanel('ActiveFilterPills', 'Remove', {
            kind: pill.kind,
            queryId: pill.queryId,
        });
    };

    const clearAll = () => {
        trackMixpanel('ActiveFilterPills', 'ClearAll', {count: pills.length});
        for (const pill of pills) {
            removeQueryById(pill.queryId);
        }
    };

    const labelFor = (pill: ActivePill): string => {
        switch (pill.kind) {
            case 'category':
                return pill.category.displayName.getText(language);
            case 'tag':
                return `#${pill.name}`;
            case 'text':
                return `"${pill.text}"`;
            case 'dates':
                return formatDateRange(pill.dates);
        }
    };

    const ariaLabelFor = (pill: ActivePill): string => {
        switch (pill.kind) {
            case 'category':
                return `Remove ${pill.category.displayName.getText(language)}`;
            case 'tag':
                return `Remove tag ${pill.name}`;
            case 'text':
                return `Remove search "${pill.text}"`;
            case 'dates':
                return `Remove dates ${formatDateRange(pill.dates)}`;
        }
    };

    return (
        <div className="active-filter-pills w-full overflow-x-auto px-4 pb-2 pt-1">
            <div className="flex flex-row items-center gap-2 w-max">
                <button
                    type="button"
                    onClick={clearAll}
                    className="shrink-0 inline-flex items-center rounded-full bg-white px-3 py-1 text-sm font-medium whitespace-nowrap hover:opacity-70 focus:outline-none"
                    style={{border: `1px solid ${BALLOON_COLOR}`, color: BALLOON_COLOR}}
                >
                    {translated('clear') || 'Clear'}
                </button>
                {pills.map((pill) => {
                    const color = pill.kind === 'category' ? pill.category.color : BALLOON_COLOR;
                    return (
                        <span
                            key={pill.queryId}
                            className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-sm font-medium whitespace-nowrap"
                            style={{border: `1px solid ${color}`, color}}
                        >
                            {labelFor(pill)}
                            {pill.kind === 'category' && (
                                <span
                                    aria-hidden="true"
                                    style={{fontFamily: 'fontAwesome', color}}
                                >
                                    {pill.category.icon}
                                </span>
                            )}
                            <button
                                type="button"
                                aria-label={ariaLabelFor(pill)}
                                className="ml-1 leading-none hover:opacity-70 focus:outline-none"
                                onClick={() => removePill(pill)}
                            >
                                ×
                            </button>
                        </span>
                    );
                })}
            </div>
        </div>
    );
};
